import { KPI } from "./../../../@core/api/models";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { GridApi, GridOptions } from "ag-grid-enterprise";
import { Observable, Subscription, combineLatest } from "rxjs";
import { ErrorService, InvestmentScenariiService, SpreadsheetColumnsService, UsersService, ChartsService } from "../../../@core/utils";
import { APIService } from "../../../@core/api";
import { ColorRendererComponent } from "../ag-grid-renderers/color-renderer.component";
import { NbDialogService, NbSpinnerModule } from "@nebular/theme";
import { filter } from "rxjs/operators";
import { Router } from "@angular/router";
import { BasicDialogComponent } from "../basic-dialog/basic-dialog.component";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";

import { AgGridAngular } from "ag-grid-angular";
import { agIconLoadingTemplate } from '../circular-spinner/circular-spinner';
import { GROUP_PERMISSION_EDIT_SCENARIO } from "../../../@core/api/models";
import { GenericButtonRendererComponent } from "../ag-grid-renderers/generic-button-renderer.component";

@Component({
  selector: "app-ag-grid-scenarii",
  templateUrl: "./ag-grid-scenarii.component.html",
  styleUrls: ["./ag-grid-scenarii.component.scss"],
  standalone: true,
  imports: [AgGridAngular, NbSpinnerModule, FontAwesomeModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgGridScenariiComponent implements OnInit, OnDestroy {
  constructor(
    private translate: TranslateService,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    private investmentScenariiService: InvestmentScenariiService,
    private errorService: ErrorService,
    private dialogService: NbDialogService,
    private usersService: UsersService,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private apiService: APIService,
    private chartsService: ChartsService
  ) {}

  private subscriptions: Subscription[] = [];
  public loadingNewScenario: boolean = false;
  public gridOptions: GridOptions = {};
  private gridApi: GridApi;
  public canEdit: boolean = false;

  ngOnInit() {
    this.subscriptions.push(
      combineLatest([
        this.usersService.hasCurrentUserPermissions([GROUP_PERMISSION_EDIT_SCENARIO]),
        this.spreadsheetColumnsService.getAgGridTranslations()
      ]).subscribe(([canEdit, agGridTranslations]) => {
        this.canEdit = canEdit;
        this.gridOptions = {
          ...this.gridOptions,
          localeText: agGridTranslations,
          overlayLoadingTemplate: agIconLoadingTemplate,
          context: this,
          rowHeight: 28,
          rowBuffer: 10, // Draw 10 rows above and below the visible rows (Performance)
          allowDragFromColumnsToolPanel: true, // Allow user to move columns from panel
          cellSelection: false, // Unable rows/columns range select
          rowSelection: "multiple", // Enable to select the entire row instead of a cell
          suppressCellFocus: true,
          getRowId: params => params.data?.id?.toString(),
          suppressPropertyNamesCheck: true,
          columnMenu: "new",
          defaultColDef: {
            sortable: true,
            resizable: true,
            editable: false,
            filter: false,
            menuTabs: [],
            suppressHeaderMenuButton: true,
            enableCellChangeFlash: true, // Flash cell when changed
          },
          // sideBar: false,
          columnDefs: [
            {
              field: "name",
              headerName: this.translate.instant("Scenario name"),
              sort: "asc",
              editable: this.canEdit
            },
            {
              field: "color",
              headerName: this.translate.instant("Color"),
              headerClass: "justify-center",
              cellRenderer: ColorRendererComponent,
              editable: this.canEdit,
              width: 100,
              suppressSizeToFit: true
            },
            {
              field: "reset",
              headerName: this.translate.instant("Actions"),
              editable: this.canEdit,
              cellRenderer: GenericButtonRendererComponent,
                  cellRendererParams: {
                  btnText: this.translate.instant("Reset"),
                  status: "danger",
                  showDeleteIcon: true,
                  handleGenericButtonClicked: (data) => this.handleGenericButtonClicked(data),
                  handleDeleteClicked: (data) => this.handleDeleteClicked(data)
                },
              cellClassRules: {
                // Can't make a scenario main if it's flagged
                "d-none": params => params.data?.is_main
              },
              headerClass: "justify-center",
              hide: !this.canEdit,
              width: 130,
              suppressSizeToFit: true
            }
          ], // Columns definition
          animateRows: true, // Row animations (when expanding)
          // Always show the column menus
          suppressMenuHide: true,
          groupHeaderHeight: 0, // Hide the columns groups in the header (Set it to 20 to show)
          // Restrict aggregations some functions (sum in an ag-grid native one)
          onGridReady: params => {
            this.gridApi = params.api;
            // Load data
            this.loadData();
          },
          // Hook executed after first data set
          onFirstDataRendered: () => {
            this.gridApi.hideOverlay();
            setTimeout(() => {
              this.gridApi.sizeColumnsToFit();
            }, 100);
          },
          onRowDataUpdated: () => {
            this.gridApi.hideOverlay();
            setTimeout(() => {
              this.gridApi.sizeColumnsToFit();
            }, 100);
          },
          onCellValueChanged: event => this.cellValueChanged(event),
          suppressContextMenu: true
        };
        this.cdRef.detectChanges();
      })
    );
  }

  private loadAgGridData(): Observable<KPI.InvestmentScenario[]> {
    return this.investmentScenariiService.scenarii$;
  }

  private timer;
  private waitTime = 500;
  public colorUpdated(scenarioId: number, color: string) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.gridApi.showLoadingOverlay();
      this.subscriptions.push(
        this.investmentScenariiService.updateScenario(scenarioId, "color", color).subscribe({
          next: () => {
            this.loadData();
          },
          error: err => {
            this.errorService.handleError(err, true);
            // Reload the old scenarii state
            this.loadData();
          }
        })
      );
    }, this.waitTime);
  }

  // Management of name, is_main and is_flagged fields update
  private cellValueChanged(event) {
    let obs: Observable<any>;
    this.gridApi.showLoadingOverlay();
    if (event.colDef.field === "is_flagged") {
      // For is_main and is_flagged fields, only one row can be true
      // The backend automatically updates it on his side, but as we wont reload the table
      // after each update, we need to update the other rows manually
      const itemsToUpdate: any[] = [];
      this.gridApi?.forEachNodeAfterFilterAndSort(function (rowNode, _index) {
        if (rowNode.data.id === event.data.id) return;
        const data = rowNode.data;
        data[event.colDef.field] = false;
        itemsToUpdate.push(data);
      });
      this.gridApi.applyTransaction({ update: itemsToUpdate })!;
      // Make the scenario main
      obs = this.investmentScenariiService.flagScenario(event.data.id);
    } else if (event.colDef.field === "name") {
      obs = this.investmentScenariiService.updateScenario(event.data.id, "name", event.newValue);
    }
    this.subscriptions.push(
      obs.subscribe(
        () => {
          this.gridApi.hideOverlay();
        },
        err => {
          this.errorService.handleError(err, true);
          // Reload the old scenarii state
          this.loadData();
        }
      )
    );
  }

  public newScenario() {
    if (this.loadingNewScenario) return;
    this.gridApi.showLoadingOverlay();
    this.loadingNewScenario = true;
    this.cdRef.detectChanges();
    this.subscriptions.push(
      this.investmentScenariiService.createScenario().subscribe({
        next: () => {
          this.loadingNewScenario = false;
          // Load scenarii after creation
          this.gridApi.showLoadingOverlay();
          this.loadData();
          this.cdRef.detectChanges();
        },
        error: err => {
          this.loadingNewScenario = false;
          this.errorService.handleError(err, true);
          this.cdRef.detectChanges();
        }
      })
    );
  }

  public handleGenericButtonClicked(data: any) {
    const resetScenarioDialog = this.dialogService.open(BasicDialogComponent, {
      context: {
        title: this.translate.instant("theme.confirmation-dialog.label"),
        content: this.translate.instant("Do you confirm that you want to reset the scenario {scenario}?", {
          scenario: data.name
        })
      },
      autoFocus: false
    });

    resetScenarioDialog.onClose
      .pipe(
        filter(confirmed => !!confirmed)
      )
      .subscribe(() => {
        this.gridApi.showLoadingOverlay();
        this.subscriptions.push(
          this.apiService.investments_investment_reset_scenarii(data.id).subscribe({
            next: () => {
              this.loadData();
              this.investmentScenariiService.refreshScenarii();
              this.chartsService.energyHasChanged();
              this.chartsService.capexHasChanged();
            },
            error: err => {
              this.errorService.handleError(err, true);
              this.loadData();
            }
          })
        );
      });
  }

  public handleDeleteClicked(data: any) {
    const deleteScenarioDialog = this.dialogService.open(BasicDialogComponent, {
      context: {
        title: this.translate.instant("theme.confirmation-dialog.label"),
        content: this.translate.instant("Do you confirm that you want to delete the scenario {scenario}?", {
          scenario: data.name
        })
      },
      autoFocus: false
    });

    deleteScenarioDialog.onClose
      .pipe(
        filter(deleted => !!deleted)
      )
      .subscribe(() => {
        this.gridApi.showLoadingOverlay();
                  this.subscriptions.push(
            this.investmentScenariiService.deleteScenario(data.id).subscribe({
              next: () => {
                this.loadData();
                this.investmentScenariiService.refreshScenarii();
                this.chartsService.energyHasChanged();
                this.chartsService.capexHasChanged();
              },
              error: err => {
                this.errorService.handleError(err, true);
                this.loadData();
              }
            })
          );
      });
  }

  private loadData() {
    this.subscriptions.push(
      this.loadAgGridData().subscribe(rowData => {
        this.gridApi?.setGridOption("rowData", rowData); // Load data rows
        this.gridApi.hideOverlay();
        this.cdRef.detectChanges();
      })
    );
  }

  public see(scenarioId: number) {
    this.investmentScenariiService.setSelectedScenario(scenarioId, "left");
    this.investmentScenariiService.setSelectedScenario(null, "right");
    this.router.navigate(["energy"]);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
}
